import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useConsult } from "@/context/ConsultContext";
import { api, formatApiError } from "@/lib/api";
import { Loader2, Rocket } from "lucide-react";

const empty = { name: "", email: "", phone: "", business: "", goals: "" };

export default function ConsultationDialog() {
  const { open, setOpen } = useConsult();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/consultation", form);
      toast.success(data.message);
      setForm(empty);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[480px] rounded-2xl" data-testid="consult-dialog">
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl grid place-items-center text-white mb-2" style={{ background: "linear-gradient(135deg,#6C2BD9,#2563EB)" }}>
            <Rocket size={22} />
          </div>
          <DialogTitle className="font-head text-2xl font-extrabold tracking-tight">Book a Free Consultation</DialogTitle>
          <DialogDescription className="text-slate-500">
            Tell us about your business and we&apos;ll craft a growth plan — no strings attached.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3.5 mt-2" data-testid="consult-form">
          <input required value={form.name} onChange={set("name")} placeholder="Full name" data-testid="consult-name" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-purple)] focus:ring-2 focus:ring-purple-100" />
          <div className="grid grid-cols-2 gap-3">
            <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" data-testid="consult-email" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-purple)] focus:ring-2 focus:ring-purple-100" />
            <input required value={form.phone} onChange={set("phone")} placeholder="Phone" data-testid="consult-phone" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-purple)] focus:ring-2 focus:ring-purple-100" />
          </div>
          <input value={form.business} onChange={set("business")} placeholder="Business / website (optional)" data-testid="consult-business" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-purple)] focus:ring-2 focus:ring-purple-100" />
          <textarea value={form.goals} onChange={set("goals")} placeholder="What are your growth goals?" rows={3} data-testid="consult-goals" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm resize-none focus:outline-none focus:border-[var(--dm-purple)] focus:ring-2 focus:ring-purple-100" />
          <button type="submit" disabled={loading} data-testid="consult-submit" className="w-full rounded-full py-3.5 font-head font-bold text-white flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-70" style={{ background: "linear-gradient(135deg,#6C2BD9,#2563EB)" }}>
            {loading ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : "Request My Free Consultation"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
